import React, { useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { Link } from 'react-router-dom';
import { useWtrSelector } from 'redux/hooks';
import { bool, string } from 'prop-types';
import classNames from 'classnames';
import { FORMAT, format } from 'utils/date';

import Button from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import {
  CalendarBooked as CalendarBookedIcon,
  Calendar as CalendarIcon,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';

import { KEY_ENTER } from 'constants/keys';
import urls from 'constants/urls';

import {
  customerSlotSelector,
  hasBookedSlot,
  isBookedSlotACollectionSlot,
} from 'redux/modules/customer-slot/selectors';
import { getCustomerSlotBranchAddress } from 'redux/modules/customer-slot/selectors/get-customer-slot-branch-address';
import { getCustomerDeliveryAddress } from 'redux/modules/customer-slot/selectors/get-customer-delivery-address';
import { getEditSlotUrl } from 'redux/modules/slot-booking/selectors/get-edit-slot-url';
import amendingOrder from 'redux/modules/trolley/selectors/amending-order';
import isSlotChangeable from 'redux/modules/trolley/selectors/slot-changeable';

import { dataLayer } from 'analytics/data-layer';
import { formatSlotTime } from 'utils/format-slot-time';

import { LinkAsButton } from '@johnlewispartnership/wtr-ingredients/ingredients/LinkAsButton';
import SlotButtonText from './SlotButtonText/SlotButtonText';
import SlotButtonAlert from '../../../../../SiteHeader/SlotButton/Alert';
import styles from './SlotButton.scss';

const SlotButton = ({ textOnly = false, shortTime = false, testId = 'desktop' }) => {
  const [hidden, setHidden] = useState(true);
  const isCollection = useWtrSelector(isBookedSlotACollectionSlot);
  const { line1, line2, name, postalCode, region, town } = useWtrSelector(state =>
    isCollection
      ? (getCustomerSlotBranchAddress(state) as Record<string, string>)
      : getCustomerDeliveryAddress(state),
  );
  const amending = useWtrSelector(amendingOrder);
  const changeSlotUrl = useWtrSelector(getEditSlotUrl);
  const isSlotBooked = useWtrSelector(hasBookedSlot);
  const slotChangeable = useWtrSelector(isSlotChangeable);
  const { slotDate, slotEndTime, slotStartTime } = useWtrSelector(customerSlotSelector);
  const blurButton = () => setHidden(true);
  const focusButton = () => setHidden(false);

  const toggleVisibility = () => {
    if (hidden) {
      dataLayer.push({
        event: 'slot_details_button_click',
      });
    }

    setHidden(!hidden);
  };

  const handleChangeSlot = () => {
    setHidden(true);

    dataLayer.push({
      event: 'change_slot_button_click',
    });
  };

  const handleClickOutside = () => {
    if (!hidden) {
      setHidden(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const { keyCode } = event;

    if (keyCode === KEY_ENTER) {
      handleChangeSlot();
    }
  };

  const handleLinkClick = () => {
    dataLayer.push({
      event: 'click_book_slot',
    });
  };

  const longDate = format(slotDate, FORMAT.LONG_2DAY_MONTH);

  const longTime = `${formatSlotTime(slotStartTime)}-${formatSlotTime(slotEndTime)}`;

  const slotButtonText = SlotButtonText({
    isSlotBooked,
    slotDetails: { slotDate, slotEndTime, slotStartTime },
    shortTime,
    showEndTime: false,
  });

  const slotButtonLabelId = `slot-button-label-${testId}`;

  return (
    <OutsideClickHandler onOutsideClick={handleClickOutside}>
      <div className={styles.wrapper} data-testid={`slot-button-${testId}`}>
        <div className={classNames({ [styles.textOnly]: textOnly })}>
          {isSlotBooked ? (
            <Button
              aria-hidden
              data-testid={`slot-details-button-${testId}`}
              startIcon={!textOnly ? <CalendarBookedIcon /> : undefined}
              label={slotButtonText}
              onClick={toggleVisibility}
              tabIndex={-1}
              theme={!textOnly ? 'primary' : 'primaryWhite'}
              width="full"
            />
          ) : (
            <LinkAsButton
              component={Link}
              data-testid={`book-slot-button-${testId}`}
              startIcon={!textOnly ? <CalendarIcon /> : undefined}
              label={slotButtonText}
              onClick={handleLinkClick}
              theme={!textOnly ? 'primary' : 'secondary'}
              to={urls.serviceSelection}
              type="button"
              width="full"
            />
          )}
        </div>
        {isSlotBooked && (
          <div
            className={classNames(styles.details, { 'sr-only': hidden })}
            data-testid={`slot-details-${testId}`}
          >
            <div className={styles.header}>
              <h3 aria-hidden="true">Your {isCollection ? 'collection' : 'delivery'} slot</h3>
            </div>
            <div className={styles.slotDateTime}>
              <span className="sr-only">Booked for:</span>
              <span>{longDate}</span>
              <span className={styles.slotTime}>{longTime}</span>
            </div>
            <div
              className={styles.deliveryDetails}
              data-cs-mask
              data-testid={`slot-address-details-${testId}`}
            >
              <span className={styles.title}>
                {isCollection ? 'Collect from:' : 'Delivery to:'}
              </span>
              {isCollection && name && <span className={styles.address}>{name}</span>}
              <span className={styles.address}>{line1}</span>
              {line2 && <span className={styles.address}>{line2}</span>}
              <span className={styles.address}>{town}</span>
              {region && <span className={styles.address}>{region}</span>}
              <span className={styles.address}>{postalCode}</span>
            </div>
            {amending && !slotChangeable ? (
              <div className={styles.alert}>
                <SlotButtonAlert />
              </div>
            ) : (
              <div className={styles.button}>
                <label className="sr-only" htmlFor="changeSlotButton" id={slotButtonLabelId}>
                  {isCollection ? 'Collection' : 'Delivery'} slot booked for {longDate} {longTime}.{' '}
                  {isCollection ? 'Collect from' : 'Delivery to'} {line1},
                  {line2 ? ` ${line2},` : ''} {town},{region ? ` ${region},` : ''} {postalCode}.
                  Change slot.
                </label>
                <LinkAsButton
                  aria-describedby={slotButtonLabelId}
                  aria-label="Change slot"
                  data-testid={`change-slot-button-${testId}`}
                  component={Link}
                  label="Change slot"
                  onBlur={blurButton}
                  onClick={handleChangeSlot}
                  onFocus={focusButton}
                  onKeyDown={handleKeyDown}
                  theme="primary"
                  to={changeSlotUrl}
                  type="button"
                  width="full"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

SlotButton.propTypes = {
  textOnly: bool,
  shortTime: bool,
  testId: string,
};

export default SlotButton;
